import type { FileType } from '@/types/file-upload'
import { extractPdfFirstPageText } from '@/utils/pdfFirstPageText'
import type { BatchDetectionResult, Confidence, FileTypeDetection } from './fileTypeDetection'
import { detectFileType, summarizeBatch } from './fileTypeDetection'

/**
 * Content-based refinement of the filename/extension detection in
 * fileTypeDetection.ts. Reads actual file bytes to resolve the cases the
 * filename detector flags as ambiguous (confidence !== 'high'):
 *
 *  - CSV: sequential-timestamp + numeric-companion detection (a TS port of
 *    backend/core sheet_classifier / header_utils value-based logic) decides
 *    timeseries vs generic table.
 *  - PDF: first-page text is classified as P&ID / PFD / prose document via
 *    title-block keywords and text structure.
 *
 * Everything FAILS OPEN: on read error, parse error, oversized file, or
 * deadline overrun the filename-based detection is returned unchanged. The
 * user still confirms via the existing type dropdown + confidence badge.
 */

const CONTENT_SNIFF_BYTES = 64 * 1024
/** pdfjs needs the whole file in memory (xref at EOF) — skip huge PDFs. */
const MAX_PDF_SNIFF_BYTES = 50 * 1024 * 1024
/** Cap per-batch content work; files past the cap keep filename detection. */
const MAX_CONTENT_SNIFF_FILES = 20
const DEFAULT_DEADLINE_MS = 5000

const MIN_TIMESERIES_ROWS = 10
const MIN_TIMESERIES_COLUMNS = 2
const SAMPLE_ROWS = 100

// ---------------------------------------------------------------------------
// Blob readers (FileReader fallback: jsdom and older Safari lack Blob.text)
// ---------------------------------------------------------------------------

function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') return blob.text()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(reader.error)
    reader.readAsText(blob)
  })
}

function readBlobBytes(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer().then(buf => new Uint8Array(buf))
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer))
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(blob)
  })
}

// ---------------------------------------------------------------------------
// Magic bytes
// ---------------------------------------------------------------------------

/** Identify the container format from a file's leading bytes. */
export function sniffContainerFormat(bytes: Uint8Array): 'pdf' | 'zip' | 'ole2' | null {
  // %PDF
  if (bytes.length >= 4 && bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) return 'pdf'
  // PK — zip container (xlsx/docx/pptx)
  if (bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4b) return 'zip'
  // OLE2 compound file (legacy .xls/.doc)
  if (bytes.length >= 4 && bytes[0] === 0xd0 && bytes[1] === 0xcf && bytes[2] === 0x11 && bytes[3] === 0xe0) return 'ole2'
  return null
}

// ---------------------------------------------------------------------------
// Timeseries table detection (port of backend/core header_utils +
// sheet_classifier — detect from DATA VALUES, not header keywords)
// ---------------------------------------------------------------------------

/**
 * Values must LOOK like a date before Date.parse gets a say — V8's parser
 * is lenient enough to read tag-like strings ("V-12", "item-0") as years,
 * which would turn any equipment-tag column into "sequential timestamps".
 * Shapes: 2024-01-02[ T...], 01/02/2024[ ...], 02-Jan-2024[ ...].
 */
const DATE_LIKE_RE =
  /^(\d{4}[-/]\d{1,2}[-/]\d{1,2}([T ].+)?|\d{1,2}[-/]\d{1,2}[-/]\d{2,4}( .+)?|\d{1,2}-[A-Za-z]{3}-\d{2,4}( .+)?)$/

function tryParseTimestamp(value: string): number | null {
  const s = value.trim()
  if (!s || !DATE_LIKE_RE.test(s)) return null
  const ms = Date.parse(s)
  return Number.isNaN(ms) ? null : ms
}

/** Detect a row-index column like 0,1,2,... or 1,2,3,... */
function looksLikeSequentialIndex(values: string[]): boolean {
  const nums: number[] = []
  for (const v of values) {
    const f = Number(v)
    if (!Number.isFinite(f) || !Number.isInteger(f)) return false
    nums.push(f)
  }
  if (nums.length < 3) return false
  if (nums[0] !== 0 && nums[0] !== 1) return false
  return nums.every((n, i) => n === nums[0] + i)
}

/**
 * Find the column whose values are sequential timestamps: ≥5 parsed
 * datetimes with ≥70% of consecutive pairs increasing. Returns the best
 * column index or null.
 */
function findTimestampColumnIndex(rows: string[][], minSequentialRatio = 0.7): number | null {
  const sample = rows.slice(0, SAMPLE_ROWS)
  if (sample.length === 0) return null
  const numCols = Math.max(...sample.map(r => r.length))

  let bestIdx: number | null = null
  let bestCount = 0
  for (let col = 0; col < numCols; col++) {
    const values = sample.map(r => r[col] ?? '')
    if (looksLikeSequentialIndex(values.filter(v => v.trim() !== ''))) continue

    const parsed: number[] = []
    for (const v of values) {
      const ts = tryParseTimestamp(v)
      if (ts !== null) parsed.push(ts)
    }
    if (parsed.length < 5) continue

    const totalPairs = parsed.length - 1
    let increasing = 0
    for (let i = 0; i < totalPairs; i++) {
      if (parsed[i + 1] > parsed[i]) increasing++
    }
    if (increasing / totalPairs < minSequentialRatio) continue
    if (increasing > bestCount) {
      bestCount = increasing
      bestIdx = col
    }
  }
  return bestIdx
}

/**
 * Require at least one mostly-numeric value column besides the timestamp —
 * timestamps + text only (e.g. event logs) are not timeseries.
 */
function hasNumericCompanions(rows: string[][], timestampColIdx: number, minRatio = 0.5): boolean {
  const sample = rows.slice(0, SAMPLE_ROWS)
  if (sample.length === 0) return false
  const numCols = Math.max(...sample.map(r => r.length))

  for (let col = 0; col < numCols; col++) {
    if (col === timestampColIdx) continue
    let numeric = 0
    let total = 0
    for (const row of sample) {
      const raw = (row[col] ?? '').trim()
      if (!raw) continue
      total++
      if (Number.isFinite(Number(raw.replace(/,/g, '')))) numeric++
    }
    if (total > 0 && numeric / total >= minRatio) return true
  }
  return false
}

/** Value-based timeseries check on parsed tabular rows (headers excluded). */
export function isTimeseriesTable(headers: string[], rows: string[][]): boolean {
  if (headers.length < MIN_TIMESERIES_COLUMNS) return false
  if (rows.length < MIN_TIMESERIES_ROWS) return false
  const tsCol = findTimestampColumnIndex(rows)
  if (tsCol === null) return false
  return hasNumericCompanions(rows, tsCol)
}

// ---------------------------------------------------------------------------
// Delimited-text parsing (minimal: quoted fields, delimiter sniff)
// ---------------------------------------------------------------------------

function detectDelimiter(line: string): string {
  let best = ','
  let bestCount = 0
  for (const d of [',', ';', '\t']) {
    const count = line.split(d).length - 1
    if (count > bestCount) {
      bestCount = count
      best = d
    }
  }
  return best
}

function splitDelimitedLine(line: string, delim: string): string[] {
  const cells: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        cur += ch
      }
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === delim) {
      cells.push(cur)
      cur = ''
    } else {
      cur += ch
    }
  }
  cells.push(cur)
  return cells
}

function parseDelimitedRows(text: string, maxRows: number): string[][] {
  const lines = text.split(/\r\n|\r|\n/).filter(l => l.trim() !== '').slice(0, maxRows)
  if (lines.length === 0) return []
  const delim = detectDelimiter(lines[0])
  return lines.map(line => splitDelimitedLine(line, delim))
}

// ---------------------------------------------------------------------------
// PDF first-page text classification
// ---------------------------------------------------------------------------

// Industry-generic title-block terms only — no tenant tag/drawing shapes
// (those would need a docs/tenant-assumptions.md row).
const PNID_TITLE_RE = /piping\s*(?:&|and)\s*instrument(?:ation)?\s*(?:diagram|dwg)|\bP\s*&\s*IDs?\b/i
// Phrase is case-insensitive (title blocks are uppercase); the bare
// abbreviation only counts in caps to avoid prose false positives.
const PFD_PHRASE_RE = /process\s*flow\s*diagram/i
const PFD_ABBREV_RE = /\bPFDs?\b/

/**
 * Classify a PDF page's extracted text. Returns null when there is no
 * usable signal (image-only scan, empty page, indeterminate structure).
 */
export function classifyPdfPageText(text: string): { type: FileType; confidence: Confidence } | null {
  const tokens = text.split(/\s+/).filter(Boolean)
  // Image-only or near-empty page: could be a scanned drawing OR a scanned
  // document — no signal either way.
  if (tokens.length < 10) return null

  const pnid = PNID_TITLE_RE.test(text)
  const pfd = PFD_PHRASE_RE.test(text) || PFD_ABBREV_RE.test(text)
  if (pnid !== pfd) {
    return pnid ? { type: 'pnid', confidence: 'high' } : { type: 'pfd', confidence: 'high' }
  }
  // Both keyword families present (e.g. a P&ID whose reference-drawing list
  // names a PFD): definitely a drawing, kind ambiguous.
  if (pnid && pfd) return { type: 'pnid', confidence: 'low' }

  // No title keywords — separate prose from drawing-annotation fragments.
  const alpha = tokens.filter(t => /[a-zA-Z]/.test(t))
  if (alpha.length === 0) return null
  const proseWords = alpha.filter(t => /^[a-z]{2,}[.,;:!?]?$/.test(t))
  if (proseWords.length / alpha.length >= 0.5) {
    return { type: 'document', confidence: 'medium' }
  }
  // Dense soup of short uppercase/numeric fragments (tags, line numbers,
  // dimensions) reads as an engineering drawing.
  const fragments = tokens.filter(t => /\d/.test(t) || /^[A-Z0-9&/\-."'()]{1,10}$/.test(t))
  if (tokens.length >= 50 && fragments.length / tokens.length >= 0.7) {
    return { type: 'pnid', confidence: 'low' }
  }
  return null
}

// ---------------------------------------------------------------------------
// Per-file refinement
// ---------------------------------------------------------------------------

function withAlternatives(base: FileTypeDetection, type: FileType, confidence: Confidence): FileTypeDetection {
  const pool: FileType[] = ['pnid', 'pfd', 'document', 'timeseries']
  const alternatives = pool.includes(type)
    ? pool.filter(t => t !== type)
    : base.alternatives
  return { fileName: base.fileName, suggestedType: type, confidence, alternatives }
}

async function refineCsv(file: File, base: FileTypeDetection): Promise<FileTypeDetection> {
  const head = await readBlobText(file.slice(0, CONTENT_SNIFF_BYTES))
  const rows = parseDelimitedRows(head, SAMPLE_ROWS + 1)
  if (rows.length < 2) return base
  const [headers, ...data] = rows
  // The sample may cut the final line mid-row — drop it.
  if (file.size > CONTENT_SNIFF_BYTES) data.pop()

  if (isTimeseriesTable(headers, data)) {
    return withAlternatives(base, 'timeseries', 'high')
  }
  // Parsed as a table but no sequential-timestamp column: generic table.
  return withAlternatives(base, 'document', 'medium')
}

async function refinePdf(file: File, base: FileTypeDetection): Promise<FileTypeDetection> {
  if (file.size > MAX_PDF_SNIFF_BYTES) return base
  const head = await readBlobBytes(file.slice(0, 8))
  if (sniffContainerFormat(head) !== 'pdf') return base
  const text = await extractPdfFirstPageText(file)
  if (!text) return base
  const verdict = classifyPdfPageText(text)
  if (!verdict) return base
  return withAlternatives(base, verdict.type, verdict.confidence)
}

async function refineDetection(file: File, base: FileTypeDetection): Promise<FileTypeDetection> {
  // A high-confidence filename detection (explicit hint / unambiguous
  // extension) wins — content only resolves the ambiguous cases.
  if (base.confidence === 'high') return base
  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
  try {
    if (ext === 'csv') return await refineCsv(file, base)
    if (ext === 'pdf') return await refinePdf(file, base)
  } catch {
    // fail open to the filename-based detection
  }
  return base
}

// ---------------------------------------------------------------------------
// Batch entry point
// ---------------------------------------------------------------------------

function withDeadline<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise(resolve => {
    const timer = setTimeout(() => resolve(fallback), ms)
    promise.then(
      value => {
        clearTimeout(timer)
        resolve(value)
      },
      () => {
        clearTimeout(timer)
        resolve(fallback)
      },
    )
  })
}

/**
 * Filename detection refined by file content, with a batch deadline.
 * Never rejects and never takes longer than deadlineMs beyond the sync
 * detection — on overrun the filename-based result is used.
 */
export async function detectBatchFileTypesFromContent(
  files: File[],
  opts?: { deadlineMs?: number },
): Promise<BatchDetectionResult> {
  const baseline = files.map(f => detectFileType(f.name))
  const work = files.map((f, i) =>
    i < MAX_CONTENT_SNIFF_FILES
      ? refineDetection(f, baseline[i]).catch(() => baseline[i])
      : Promise.resolve(baseline[i]),
  )
  const refined = await withDeadline(Promise.all(work), opts?.deadlineMs ?? DEFAULT_DEADLINE_MS, baseline)
  return summarizeBatch(refined)
}
